import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useCountDown, useI18N, useResponsive } from "@sundae/react-hooks";
import { Text, Tooltip } from "@sundae/ui-toolkit";
import { FC, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";

import { TASTE_TEST_DETAIL_COUNTDOWN_ID } from "../../../constants/page.constants";
import { Status } from "../../../gql/generated/taste-test.sdk";
import TasteTestDuration from "../../../i18n/components/taste-test/TasteTestDuration";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { safariSafeDate } from "../../../utils/dates.utils";

export const TasteTestCountdown: FC = () => {
  const { t } = useI18N("taste-test");
  const { lg } = useResponsive();
  const {
    handlers: { refetchTasteTest, setIsCountdownActive },
    state: {
      tasteTestStatus: { isTasteTestPending },
      tasteTest,
    },
  } = useTasteTestContext();
  const safariSafe_endDate = safariSafeDate(tasteTest?.endDate.format);
  const safariSafe_startDate = safariSafeDate(tasteTest?.openDate.format);
  const targetDate = isTasteTestPending ? safariSafe_startDate : safariSafe_endDate;

  const [, { days, hours, minutes, seconds }] = useCountDown({
    targetDate,
  });

  const countdownNode = document.getElementById(TASTE_TEST_DETAIL_COUNTDOWN_ID);
  const renderContent = useCallback(() => {
    if (!days && !hours && !minutes && !seconds) {
      /**
       * make sure to show the correct status when the countdown reaches 0. Since we're refetching a taste test not _that_ often,
       * we can be sure that if the countdown reaches 0 and the status is still "LastCall", it's safe to show "Closing" instead.
       */
      const status = tasteTest?.status === Status.LastCall ? Status.Closing : tasteTest?.status;
      return (
        <div className="flex flex-col items-center">
          <Text
            className="[text-shadow:_0_1px_0_rgb(0_0_0_/_40%)]"
            tag="h3"
            variant="forceLight"
            size={lg ? "4xl" : "2xl"}
            weight="medium"
          >
            {t(`status.${status}`)}
          </Text>

          <Text tag="span" variant="forceLight" className="opacity-60">
            {t("status", { ns: "glossary" })}

            <Tooltip
              trigger={
                <InformationCircleIcon className="ml-1 inline h-3 w-3 cursor-help !text-current" />
              }
              tooltipLabel={t(`status.${status}`, {
                context: "tooltip",
              })}
            />
          </Text>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center">
        <Text
          className="text-center [text-shadow:_0_1px_0_rgb(0_0_0_/_40%)]"
          tag="h3"
          variant="forceLight"
          size={lg ? "4xl" : "2xl"}
          weight="medium"
        >
          {days}D {hours}H {minutes}M {seconds}S
        </Text>

        <Text tag="span" variant="forceLight" className="opacity-60">
          {t("detail.countdown.title")}
          <Tooltip
            trigger={
              <InformationCircleIcon className="ml-1 inline h-3 w-3 cursor-help !text-current" />
            }
            tooltipLabel={<TasteTestDuration />}
          />
        </Text>
      </div>
    );
  }, [days, hours, minutes, seconds, lg, tasteTest?.status]);

  /**
   * To avoid pending updates from our API that might not be in sync with the countdown (TasteTestStatus),
   * we will set the flag for the active countdown to false if the countdown reaches 0.
   * This is more of a safety measure to avoid any potential issues.
   */
  useEffect(() => {
    if (!days && !hours && !minutes && !seconds) {
      setIsCountdownActive(false);

      setTimeout(() => {
        refetchTasteTest?.(); // refetch the taste test 1s (threshold) after the countdown ran out to get the latest status update
      }, 1000); //
    }
  }, [days, hours, minutes, seconds]);

  // rendered in Portal to avoid rerenders on the whole parent
  return countdownNode && createPortal(renderContent(), countdownNode);
};

export const TasteTestCountdownNode: FC = () => (
  <div className="relative z-10" id={TASTE_TEST_DETAIL_COUNTDOWN_ID} />
);
